import axios from 'axios';
import { pb } from './pocketbase';
import { offlineQueue, isOfflineError } from './offlineQueue';
import { installSessionRecovery } from './session';

const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
});

// Inject the PocketBase auth token on every request
api.interceptors.request.use((config) => {
  const token = pb.authStore.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A 401 renews the session, or waits for the user to sign in again over the
// current page, then replays the call (see services/session.js).
installSessionRecovery(api);

/**
 * Wrap a mutation so that a network failure queues it instead of losing it.
 * HTTP errors still propagate — the server answered, so it is a real error.
 */
function offlineAware(op, direct) {
  return async (...args) => {
    try {
      return await direct(...args);
    } catch (err) {
      if (!isOfflineError(err)) throw err;
      await offlineQueue.enqueue(op, args);
      return { _queuedOffline: true };
    }
  };
}

// ---------- Reports ----------

export async function listReports(params = {}) {
  const { data } = await api.get('/api/reports', { params });
  return data; // { items, total, limit, offset }
}

export async function createReport(payload) {
  const { data } = await api.post('/api/reports', payload);
  return data;
}

export async function getReport(id) {
  const { data } = await api.get(`/api/reports/${id}`);
  return data;
}

async function updateReportDirect(id, payload) {
  const { data } = await api.patch(`/api/reports/${id}`, payload);
  return data;
}

export const updateReport = offlineAware('updateReport', updateReportDirect);

export async function deleteReport(id) {
  await api.delete(`/api/reports/${id}`);
}

// ---------- Steps ----------

export async function createStep(reportId, payload) {
  const { data } = await api.post(`/api/reports/${reportId}/steps`, payload);
  return data;
}

async function updateStepDirect(reportId, stepId, payload) {
  const { data } = await api.patch(`/api/reports/${reportId}/steps/${stepId}`, payload);
  return data;
}

export const updateStep = offlineAware('updateStep', updateStepDirect);

export async function deleteStep(reportId, stepId) {
  await api.delete(`/api/reports/${reportId}/steps/${stepId}`);
}

// ---------- Images ----------

const UPLOAD_TIMEOUT_MS = 90_000;

async function uploadImageDirect(reportId, stepId, file, comment = '', order = 0) {
  // `file` is a File when live, or a {blob, name} record replayed from the queue.
  const blob = file instanceof Blob ? file : file.blob;
  const name = file instanceof File ? file.name : (file.name || 'photo.jpg');
  const formData = new FormData();
  formData.append('file', blob, name);
  formData.append('comment', comment);
  formData.append('order', order);
  const { data } = await api.post(
    `/api/reports/${reportId}/steps/${stepId}/images`,
    formData,
    // A photo stalled on a weak link must end in the offline queue rather than
    // spin forever: without a timeout the request never failed at all.
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS }
  );
  return data;
}

/** Photos are the one write that must survive a dead zone — queue with its bytes. */
export async function uploadImage(reportId, stepId, file, comment = '', order = 0) {
  try {
    return await uploadImageDirect(reportId, stepId, file, comment, order);
  } catch (err) {
    if (!isOfflineError(err)) throw err;
    const record = { blob: file, name: file.name || 'photo.jpg' };
    await offlineQueue.enqueue('uploadImage', [reportId, stepId, record, comment, order]);
    // Optimistic placeholder so the shot appears immediately in the wizard.
    return {
      id: `pending-${Date.now()}-${order}`,
      filename: '',
      original_filename: record.name,
      annotated_filename: '',
      comment,
      order,
      _pendingUpload: true,
      _localUrl: URL.createObjectURL(file),
    };
  }
}

export async function listStepImages(reportId, stepId) {
  const { data } = await api.get(`/api/reports/${reportId}/steps/${stepId}/images`);
  return data;
}

// ---------- Mobile upload by QR code ----------

/** Mint the link the desktop shows as a QR code (write-only, one step, 30 min). */
export async function createStepUploadLink(reportId, stepId) {
  const { data } = await api.post(
    `/api/reports/${reportId}/steps/${stepId}/upload-link`
  );
  return data;
}

// The phone has no session, so these two calls go through a bare client: the
// shared instance would attach a token it does not have, and its interceptors
// are written for a signed-in app.
const publicApi = axios.create({ baseURL: API_URL });

export async function getMobileUploadInfo(token) {
  const { data } = await publicApi.get(`/api/upload/${token}`);
  return data;
}

export async function sendMobileUpload(token, files, onProgress) {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file, file.name || 'photo.jpg'));
  const { data } = await publicApi.post(`/api/upload/${token}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
  return data;
}

async function updateImageDirect(reportId, stepId, imageId, payload) {
  const { data } = await api.patch(
    `/api/reports/${reportId}/steps/${stepId}/images/${imageId}`,
    payload
  );
  return data;
}

export const updateImage = offlineAware('updateImage', updateImageDirect);

export async function uploadAnnotatedImage(reportId, stepId, imageId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(
    `/api/reports/${reportId}/steps/${stepId}/images/${imageId}/annotated`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

// Cropped render of a photo; the original stays on the server, untouched.
export async function uploadCroppedImage(reportId, stepId, imageId, file, frame) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('crop', JSON.stringify(frame));
  const { data } = await api.post(
    `/api/reports/${reportId}/steps/${stepId}/images/${imageId}/cropped`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function resetCroppedImage(reportId, stepId, imageId) {
  const { data } = await api.delete(`/api/reports/${reportId}/steps/${stepId}/images/${imageId}/cropped`);
  return data;
}

export async function deleteImage(reportId, stepId, imageId) {
  await api.delete(`/api/reports/${reportId}/steps/${stepId}/images/${imageId}`);
}

/** Put a step's photos in this order (every photo id of the step, once). */
export async function reorderImages(reportId, stepId, imageIds) {
  const { data } = await api.put(
    `/api/reports/${reportId}/steps/${stepId}/images/order`,
    { image_ids: imageIds },
  );
  return data;
}

// ---------- Client link (report read online by its recipient) ----------

export async function getShareLink(reportId) {
  const { data } = await api.get(`/api/reports/${reportId}/share-link`);
  return data.link;
}

export async function createShareLink(reportId) {
  const { data } = await api.post(`/api/reports/${reportId}/share-link`);
  return data.link;
}

export async function revokeShareLink(reportId) {
  await api.delete(`/api/reports/${reportId}/share-link`);
}

// ---------- Report sent to its client by e-mail ----------

export async function getReportEmailDraft(reportId) {
  const { data } = await api.get(`/api/reports/${reportId}/email`);
  return data;
}

export async function previewReportEmail(reportId, payload) {
  const { data } = await api.post(`/api/reports/${reportId}/email/preview`, payload);
  return data.html;
}

export async function sendReportEmail(reportId, payload) {
  const { data } = await api.post(`/api/reports/${reportId}/email`, payload);
  return data;
}

// ---------- Checklist ----------

async function updateChecklistItemDirect(reportId, itemId, payload) {
  const { data } = await api.patch(
    `/api/reports/${reportId}/checklist/${itemId}`,
    payload
  );
  return data;
}

export const updateChecklistItem = offlineAware('updateChecklistItem', updateChecklistItemDirect);

/** A checkpoint for this report only (not offered on the others). */
export async function addChecklistItem(reportId, label) {
  const { data } = await api.post(`/api/reports/${reportId}/checklist`, { label });
  return data;
}

export async function deleteChecklistItem(reportId, itemId) {
  await api.delete(`/api/reports/${reportId}/checklist/${itemId}`);
}

// ---------- Clients (one list: billed or visited, per report) ----------

export async function listCustomers() {
  const { data } = await api.get('/api/reports/customers');
  return data;
}

export async function createCustomer(payload) {
  const { data } = await api.post('/api/reports/customers', payload);
  return data;
}

export async function updateCustomer(id, payload) {
  const { data } = await api.patch(`/api/reports/customers/${id}`, payload);
  return data;
}

export async function deleteCustomer(id) {
  await api.delete(`/api/reports/customers/${id}`);
}

// ---------- Admin Step Templates ----------

export async function adminListStepTemplates() {
  const { data } = await api.get('/api/admin/step-templates');
  return data;
}

export async function adminCreateStepTemplate(payload) {
  const { data } = await api.post('/api/admin/step-templates', payload);
  return data;
}

export async function adminUpdateStepTemplate(templateId, payload) {
  const { data } = await api.patch(`/api/admin/step-templates/${templateId}`, payload);
  return data;
}

export async function adminDeleteStepTemplate(templateId) {
  await api.delete(`/api/admin/step-templates/${templateId}`);
}

// ---------- Leads ----------

export async function listLeads(params = {}) {
  const { data } = await api.get('/api/leads', { params });
  return data;
}

export async function createLead(payload) {
  const { data } = await api.post('/api/leads', payload);
  return data;
}

export async function updateLead(id, payload) {
  const { data } = await api.patch(`/api/leads/${id}`, payload);
  return data;
}

export async function deleteLead(id) {
  await api.delete(`/api/leads/${id}`);
}

export async function convertLead(id, payload = {}) {
  const { data } = await api.post(`/api/leads/${id}/convert`, payload);
  return data;
}

// ---------- Appointments ----------

// ICS feed of the user's appointments (calendar-app subscription).
export async function getCalendarFeed() {
  const { data } = await api.get('/api/calendar/feed');
  return data;
}

export async function renewCalendarFeed() {
  const { data } = await api.post('/api/calendar/feed');
  return data;
}

export async function disableCalendarFeed() {
  await api.delete('/api/calendar/feed');
}

export async function listAppointments(params = {}, config = {}) {
  const { data } = await api.get('/api/appointments', { params, ...config });
  return data;
}

export async function createAppointment(payload) {
  const { data } = await api.post('/api/appointments', payload);
  return data;
}

export async function getAppointment(id) {
  const { data } = await api.get(`/api/appointments/${id}`);
  return data;
}

export async function updateAppointment(id, payload) {
  const { data } = await api.patch(`/api/appointments/${id}`, payload);
  return data;
}

export async function deleteAppointment(id) {
  await api.delete(`/api/appointments/${id}`);
}

export async function createReportFromAppointment(id) {
  const { data } = await api.post(`/api/appointments/${id}/create-report`);
  return data;
}

// ---------- Appointment confirmation (CGV) ----------

export async function getConfirmationDraft(appointmentId) {
  const { data } = await api.get(`/api/appointments/${appointmentId}/confirmation-draft`);
  return data;
}

// Returns the full appointment, so the caller can swap the row in place.
export async function requestConfirmation(appointmentId, recipients, clientNote) {
  const payload = { recipients };
  // Omitted (undefined) leaves the stored note untouched on the server.
  if (clientNote !== undefined) payload.client_note = clientNote;
  const { data } = await api.post(`/api/appointments/${appointmentId}/confirmation-requests`, payload);
  return data;
}

export async function adminUploadTerms(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/api/admin/settings/terms', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// The PDF sits behind the auth header, so it comes as a blob for openPdfInNewTab.
export async function adminDownloadTerms() {
  const response = await api.get('/api/admin/settings/terms', { responseType: 'blob' });
  return response.data;
}

// ---------- PDF ----------

export async function downloadPdf(reportId) {
  const response = await api.get(`/api/reports/${reportId}/pdf`, {
    responseType: 'blob',
  });
  return response.data;
}

// ---------- Company ----------

export async function getCompanyInfo() {
  const { data } = await api.get('/api/company');
  return data;
}

// ---------- User ----------

export async function getMe() {
  const { data } = await api.get('/api/users/me');
  return data;
}

export function getUploadUrl(reportId, filename) {
  return `${API_URL}/uploads/${reportId}/${filename}`;
}

export async function fetchImageAsBlob(reportId, filename) {
  const response = await api.get(`/uploads/${reportId}/${filename}`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
}

// ---------- Admin Settings ----------

export async function adminGetSettings() {
  const { data } = await api.get('/api/admin/settings');
  return data;
}

export async function adminUpdateSettings(payload) {
  const { data } = await api.patch('/api/admin/settings', payload);
  return data;
}

export async function adminUploadLogo(file) {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/api/admin/settings/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ---------- Admin ----------

export async function adminListUsers() {
  const { data } = await api.get('/api/admin/users');
  return data;
}

export async function adminCreateUser(payload) {
  const { data } = await api.post('/api/admin/users', payload);
  return data;
}

export async function adminUpdateUser(userId, payload) {
  const { data } = await api.patch(`/api/admin/users/${userId}`, payload);
  return data;
}

export async function adminDeleteUser(userId) {
  await api.delete(`/api/admin/users/${userId}`);
}

// ---------- Invoices ----------

export async function listInvoices(reportId) {
  const { data } = await api.get('/api/invoices', {
    params: reportId ? { report_id: reportId } : undefined,
  });
  return data;
}

export async function getInvoice(id) {
  const { data } = await api.get(`/api/invoices/${id}`);
  return data;
}

export async function createInvoice(payload) {
  const { data } = await api.post('/api/invoices', payload);
  return data;
}

export async function createInvoiceFromReport(reportId, payload) {
  const { data } = await api.post(`/api/reports/${reportId}/invoice`, payload);
  return data;
}

export async function retryInvoice(id) {
  const { data } = await api.post(`/api/invoices/${id}/retry`);
  return data;
}

export async function sendInvoice(id) {
  const { data } = await api.post(`/api/invoices/${id}/send`);
  return data;
}

export async function markInvoicePaid(id, amount) {
  const { data } = await api.post(`/api/invoices/${id}/mark-paid`, amount ? { amount } : {});
  return data;
}

export async function refreshInvoice(id) {
  const { data } = await api.post(`/api/invoices/${id}/refresh`);
  return data;
}

export async function deleteInvoice(id) {
  await api.delete(`/api/invoices/${id}`);
}

export async function listBillingPresets() {
  const { data } = await api.get('/api/invoices/presets');
  return data;
}

// ---------- Admin Billing Presets ----------

export async function adminListPresets() {
  const { data } = await api.get('/api/admin/presets');
  return data;
}

export async function adminCreatePreset(payload) {
  const { data } = await api.post('/api/admin/presets', payload);
  return data;
}

export async function adminUpdatePreset(presetId, payload) {
  const { data } = await api.patch(`/api/admin/presets/${presetId}`, payload);
  return data;
}

export async function adminDeletePreset(presetId) {
  await api.delete(`/api/admin/presets/${presetId}`);
}

export async function adminTestInvoiceNinja(payload) {
  const { data } = await api.post('/api/admin/settings/invoiceninja/test', payload);
  return data;
}

// ---------- AI assistance ----------

export async function getAiStatus() {
  const { data } = await api.get('/api/ai/status');
  return data;
}

export async function aiEnrich(reportId, payload) {
  const { data } = await api.post(`/api/reports/${reportId}/ai/enrich`, payload);
  return data;
}

export async function aiConclusion(reportId, payload) {
  const { data } = await api.post(`/api/reports/${reportId}/ai/conclusion`, payload);
  return data;
}

export async function aiProofread(reportId, payload) {
  const { data } = await api.post(`/api/reports/${reportId}/ai/proofread`, payload);
  return data;
}

export async function aiReview(reportId) {
  const { data } = await api.post(`/api/reports/${reportId}/ai/review`);
  return data;
}

export async function adminTestAi(payload) {
  const { data } = await api.post('/api/admin/settings/ai/test', payload);
  return data;
}

export async function adminListAiModels(payload) {
  const { data } = await api.post('/api/admin/settings/ai/models', payload);
  return data;
}

// ---------- GDPR / audit (admin) ----------

export async function adminAuditLog(params = {}) {
  const { data } = await api.get('/api/admin/audit', { params });
  return data;
}

export async function gdprExport(kind, id) {
  const { data } = await api.get(`/api/admin/gdpr/${kind}/${id}/export`);
  return data;
}

export async function gdprAnonymize(kind, id) {
  const { data } = await api.post(`/api/admin/gdpr/${kind}/${id}/anonymize`);
  return data;
}

export async function adminAiUsage(days = 30) {
  const { data } = await api.get('/api/admin/ai/usage', { params: { days } });
  return data;
}

export async function adminAiCostEstimate({ avgChars, avgImages } = {}) {
  const { data } = await api.get('/api/admin/ai/cost-estimate', {
    params: { avg_chars: avgChars, avg_images: avgImages },
  });
  return data;
}

// ---------- Admin Checklist Templates ----------

export async function adminListChecklistTemplates() {
  const { data } = await api.get('/api/admin/checklist-templates');
  return data;
}

export async function adminCreateChecklistTemplate(payload) {
  const { data } = await api.post('/api/admin/checklist-templates', payload);
  return data;
}

export async function adminUpdateChecklistTemplate(templateId, payload) {
  const { data } = await api.patch(`/api/admin/checklist-templates/${templateId}`, payload);
  return data;
}

export async function adminDeleteChecklistTemplate(templateId) {
  await api.delete(`/api/admin/checklist-templates/${templateId}`);
}

// ---------- Offline queue wiring ----------

/**
 * How each queued operation is replayed. Keys must match the `op` names used
 * when enqueuing; the direct (non-queueing) variants are used on purpose, so a
 * replay that fails while still offline stops the flush instead of re-queuing.
 */
const OFFLINE_EXECUTORS = {
  updateReport: updateReportDirect,
  updateStep: updateStepDirect,
  updateChecklistItem: updateChecklistItemDirect,
  updateImage: updateImageDirect,
  uploadImage: uploadImageDirect,
};

/** Replay everything pending. Safe to call repeatedly (self-guarded). */
export function flushOfflineQueue() {
  return offlineQueue.flush(OFFLINE_EXECUTORS);
}

export function subscribeOfflineQueue(fn) {
  return offlineQueue.subscribe(fn);
}

export function pendingOfflineCount() {
  return offlineQueue.count();
}

/** Drop cached personal data + queued writes (called on logout). */
export async function clearOfflineData() {
  await offlineQueue.clear();
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_API_CACHE' });
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flushOfflineQueue().catch(() => {}); });

  // A write queued because the link was too weak (not truly offline) gets no
  // `online` event to replay it: it used to wait for a reload or a manual
  // "Sync now" — the photo that "reappeared much later". Retry on a timer
  // while something is pending, and whenever the app comes back to the front.
  const retryPending = () => {
    if (!navigator.onLine) return;
    offlineQueue.count()
      .then((n) => (n > 0 ? flushOfflineQueue() : null))
      .catch(() => {});
  };
  if (process.env.NODE_ENV !== 'test') setInterval(retryPending, 20_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') retryPending();
  });
}
